import { clear, element, forEachElement } from "../util/Utils";
import { SortingForm } from "../sorting/SortingForm";
import type { Appendable } from "../util/Appendable";
import type { FilterSectionLoadedEvent } from "../multifiltering/FilterTypes";
import FilterSwitchBanner from "../filterBanner/FilterSwitchBanner";
import { events } from "../events";
import {
  NextBestActionComponent,
  resetNBAScrollTracking,
  trackNBAScrolling,
  trackNBAVisibility,
} from "../nextBestAction/NextBestActionComponent";
import { FacetValueTagsComponent } from "../facetvaluetags/FacetValueTagsComponent";
import { isNotActive } from "../toggle/Toggle";
import { isMobile } from "../util/Breakpoint";

const CLASS_REFINEMENT_BAR = "find_refinementBar";
const SELECTOR_REFINEMENT_BAR = `.${CLASS_REFINEMENT_BAR}`;

const ID_SORTING_FORM_ANCHOR = "heureka_desktopSorting";
const SELECTOR_SORTING_FORM_ANCHOR = `#${ID_SORTING_FORM_ANCHOR}`;
const ID_FACET_VALUE_TAGS_ANCHOR = "find_facetValueTags";
const SELECTOR_FACET_VALUE_TAGS_ANCHOR = `#${ID_FACET_VALUE_TAGS_ANCHOR}`;
const ID_BANNER_ANCHOR = "heureka_filterBanner";
const SELECTOR_BANNER_ANCHOR = `#${ID_BANNER_ANCHOR}`;
const SELECTOR_FACET_VALUE_TAGS = `${SELECTOR_REFINEMENT_BAR} ${SELECTOR_FACET_VALUE_TAGS_ANCHOR}`;
const SCROLL_TICK_LENGTH_MS = 50;
const SCROLL_STOP_DELAY_MS = 1000; /*                                                     */

export class RefinementBar {
  /*               */
  protected constructor(
    private readonly sortingFormAnchor: HTMLElement | null,
    private readonly facetValueTagsAnchor: HTMLElement | null,
    private readonly filterBannerAnchor: HTMLElement | null,
  ) {}

  static #create(elem: HTMLElement) {
    /*                                               */
    if (!document.getElementById(ID_BANNER_ANCHOR) && document.querySelector(SELECTOR_REFINEMENT_BAR)) {
      const ottoSwitchBannerAnchor = document.createElement("div");
      ottoSwitchBannerAnchor.id = ID_BANNER_ANCHOR;
      document.querySelector(SELECTOR_REFINEMENT_BAR)?.append(ottoSwitchBannerAnchor);
    }
    return new RefinementBar(
      element(SELECTOR_SORTING_FORM_ANCHOR, elem),
      element(SELECTOR_FACET_VALUE_TAGS_ANCHOR, elem),
      element(SELECTOR_BANNER_ANCHOR, elem),
    );
  }

  static facetValueTagsElement(rootElement?: ParentNode | null): HTMLElement | null {
    return element(SELECTOR_FACET_VALUE_TAGS, rootElement);
  }

  /*                  */

  static forEach(callback: (input: RefinementBar, index: number) => void, rootElement?: ParentNode) {
    forEachElement<HTMLElement>(
      SELECTOR_REFINEMENT_BAR,
      (elem, index) => callback(RefinementBar.#create(elem), index),
      rootElement,
    );
  }

  /*               */

  static lockAll() {
    RefinementBar.forEach((refinementBar) => refinementBar.lock());
  }

  static unlockAll() {
    RefinementBar.forEach((refinementBar) => refinementBar.unlock());
  }

  static initAllDeprecated(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    RefinementBar.forEach((refinementBar) => refinementBar.initDeprecated(), rootElement);
    events.refinementBarLoaded();
  }

  initDeprecated() {
    const form: SortingForm | undefined = SortingForm.template();
    if (form) {
      this.sortingForm = form.clone();
    }

    this.clearFilterBanner();

    if (isNotActive("HEUREKA_2117_MOUNT_NBF_DIRECTLY_IN_REFINEMENT_BAR")) {
      this.clearRefinementForm();
      let nextBestActionComponent: NextBestActionComponent;
      if (NextBestActionComponent.couldBeVisible()) {
        nextBestActionComponent = NextBestActionComponent.template()?.clone().init();
        this.addToRefinementForm(nextBestActionComponent);
        if (isMobile()) {
          this.setupNBAScrollTracking(nextBestActionComponent);
          nextBestActionComponent?.trackInitialVisibilityNbaDeprecated();
        }
      } else {
        const facetValueTagsComponent = FacetValueTagsComponent.template()?.clone().init();
        this.addToRefinementForm(facetValueTagsComponent);
      }
    }
    this.addToFilterBannerDeprecated(FilterSwitchBanner.fromTemplate()?.clone());

    /*                                                                                                               */
    this.hideRefinementBarPlaceholder();
  }

  static initAll(event: FilterSectionLoadedEvent, rootElement?: ParentNode) {
    RefinementBar.forEach((refinementBar) => refinementBar.init(), rootElement);
    events.refinementBarLoaded();
  }

  init() {
    const form: SortingForm | undefined = SortingForm.template();
    if (form) {
      this.sortingForm = form;
    }
    this.addToFilterBanner(FilterSwitchBanner.fromTemplate()?.init());

    /*                                                                                                               */
    this.hideRefinementBarPlaceholder();
  }

  private setupNBAScrollTracking(nextBestActionComponent: NextBestActionComponent) {
    const nbaScrollContainer = nextBestActionComponent?.tags?.querySelector(
      ".hc_nextBestAction__values",
    ) as HTMLElement;
    if (nbaScrollContainer) {
      const isScrollable = nbaScrollContainer.scrollWidth > nbaScrollContainer.clientWidth;
      if (isScrollable) {
        let trackingTimeout: ReturnType<typeof setTimeout> | null = null;
        let collectionTimeout: ReturnType<typeof setTimeout> | null = null;

        nbaScrollContainer.addEventListener("change", () => {
          /*                                                                                          */
          if (trackingTimeout) {
            clearTimeout(trackingTimeout);
            trackingTimeout = null;
            trackNBAVisibility();
          }
        });

        nbaScrollContainer.addEventListener("scroll", () => {
          /*                                                             */
          if (trackingTimeout) {
            clearTimeout(trackingTimeout);
            trackingTimeout = null;
          }

          /*                                          */
          if (!collectionTimeout) {
            collectionTimeout = setTimeout(() => {
              trackNBAScrolling(nextBestActionComponent);
              collectionTimeout = null;
            }, SCROLL_TICK_LENGTH_MS);
          }

          /*                                                      */
          trackingTimeout = setTimeout(() => {
            trackNBAVisibility();
            trackingTimeout = null;
          }, SCROLL_STOP_DELAY_MS);
        });
      }
    }
  }

  private hideRefinementBarPlaceholder() {
    const refinementBarPlaceholder = document.getElementById("heureka_refinementBar__placeholder");
    if (
      refinementBarPlaceholder &&
      !refinementBarPlaceholder.classList.contains("heureka_refinementBar__placeholder--hidden")
    ) {
      refinementBarPlaceholder.classList.add("heureka_refinementBar__placeholder--hidden");
    }
  }

  /*                       */

  public lock() {
    this.sortingFormAnchor?.classList.add("find_refinementBar--loading");
    this.facetValueTagsAnchor?.classList.add("find_refinementBar--loading");
    this.filterBannerAnchor?.classList.add("find_refinementBar--loading");
    return this;
  }

  public unlock() {
    this.sortingFormAnchor?.classList.remove("find_refinementBar--loading");
    this.facetValueTagsAnchor?.classList.remove("find_refinementBar--loading");
    this.filterBannerAnchor?.classList.remove("find_refinementBar--loading");
    return this;
  }

  set sortingForm(form: SortingForm) {
    const anchor = this.sortingFormAnchor;
    if (anchor) {
      clear(anchor);
      anchor.appendChild(form.form);
    }
  }

  private clearRefinementForm() {
    const anchor = this.facetValueTagsAnchor;
    if (anchor) {
      clear(anchor);
    }
    /*                                                        */
    if (NextBestActionComponent.couldBeVisible()) {
      resetNBAScrollTracking();
    }
  }

  private addToRefinementForm(form: Appendable | undefined) {
    const anchor = this.facetValueTagsAnchor;
    if (anchor && form) {
      form.appendTo(anchor);
    }
  }

  private addToFilterBanner(elem: Appendable | undefined) {
    const anchor = this.filterBannerAnchor;
    if (anchor && elem) {
      clear(anchor);
      elem.appendTo(anchor);
    }
  }

  private addToFilterBannerDeprecated(elem: Appendable | undefined) {
    const anchor = this.filterBannerAnchor;
    if (anchor && elem) {
      elem.appendTo(anchor);
    }
  }

  private clearFilterBanner() {
    const anchor = this.filterBannerAnchor;
    if (anchor) {
      clear(anchor);
    }
  }
}
